import React, { useState } from 'react';
import { Bell, Globe, Shield, Palette, ChevronRight } from 'lucide-react';
import { PageHeader, PageShell, SectionCard } from '../components/PeecUI';

interface SettingsPageProps {
  workspaceId: string;
}

const SettingsPage: React.FC<SettingsPageProps> = () => {
  const [notifications, setNotifications] = useState({ weekly: true, product: false, alerts: true });
  const [crawlFreq, setCrawlFreq] = useState('daily');

  const toggle = (key: keyof typeof notifications) =>
    setNotifications(n => ({ ...n, [key]: !n[key] }));

  const Toggle: React.FC<{ checked: boolean; onChange: () => void; label: string }> = ({ checked, onChange, label }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-700">{label}</span>
      <button
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-1 outline-none ${checked ? 'bg-violet-600' : 'bg-slate-200'}`}
      >
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-1'}`} />
      </button>
    </div>
  );

  return (
    <PageShell>
      <PageHeader title="设置" description="管理工作空间偏好、通知和数据采集配置" />

      <div className="grid gap-5 xl:grid-cols-2">
        <SectionCard title="通知设置" subtitle="选择您希望接收的通知类型">
          <div className="px-5 pb-4">
            <div className="flex items-center gap-2 mb-3 mt-1">
              <Bell size={15} className="text-slate-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">邮件通知</span>
            </div>
            <Toggle checked={notifications.weekly} onChange={() => toggle('weekly')} label="每周数据摘要" />
            <Toggle checked={notifications.product} onChange={() => toggle('product')} label="产品更新与新功能" />
            <Toggle checked={notifications.alerts} onChange={() => toggle('alerts')} label="品牌可见度告警" />
          </div>
        </SectionCard>

        <SectionCard title="数据采集频率" subtitle="设置 AI 可见度数据的采集周期">
          <div className="px-5 pb-4 mt-1">
            <div className="flex items-center gap-2 mb-3">
              <Globe size={15} className="text-slate-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">采集配置</span>
            </div>
            {['hourly', 'daily', 'weekly'].map((freq) => (
              <label key={freq} className="flex items-center gap-3 py-2.5 cursor-pointer border-b border-slate-100 last:border-0">
                <input
                  type="radio"
                  name="crawl_freq"
                  value={freq}
                  checked={crawlFreq === freq}
                  onChange={() => setCrawlFreq(freq)}
                  className="accent-violet-600"
                />
                <div>
                  <div className="text-sm font-medium text-slate-800">
                    {freq === 'hourly' ? '每小时' : freq === 'daily' ? '每天' : '每周'}
                  </div>
                  <div className="text-xs text-slate-400">
                    {freq === 'hourly' ? '适合高频监控场景，消耗配额较多' : freq === 'daily' ? '推荐设置，平衡数据时效与配额消耗' : '适合预算有限的长期追踪'}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="安全设置" subtitle="管理访问控制与会话安全">
          <div className="px-5 pb-4 mt-1">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={15} className="text-slate-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">访问安全</span>
            </div>
            {[
              { label: '双因素认证 (2FA)', desc: '已启用 — Authenticator App', action: '管理' },
              { label: '会话超时', desc: '30 分钟无操作后自动退出', action: '修改' },
              { label: '登录历史', desc: '查看最近 30 天的登录记录', action: '查看' },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                <div>
                  <div className="text-sm font-medium text-slate-800">{item.label}</div>
                  <div className="text-xs text-slate-400">{item.desc}</div>
                </div>
                <button className="text-xs font-medium text-violet-600 hover:text-violet-800 flex items-center gap-0.5">
                  {item.action} <ChevronRight size={12} />
                </button>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="界面偏好" subtitle="自定义显示和语言设置">
          <div className="px-5 pb-4 mt-1">
            <div className="flex items-center gap-2 mb-3">
              <Palette size={15} className="text-slate-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">显示设置</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <div className="text-sm font-medium text-slate-800">界面语言</div>
                <div className="text-xs text-slate-400">当前：中文</div>
              </div>
              <select className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-400">
                <option value="zh">中文</option>
                <option value="en">English</option>
              </select>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <div className="text-sm font-medium text-slate-800">时区</div>
                <div className="text-xs text-slate-400">当前：Asia/Hong_Kong (UTC+8)</div>
              </div>
              <button className="text-xs font-medium text-violet-600 hover:text-violet-800 flex items-center gap-0.5">
                修改 <ChevronRight size={12} />
              </button>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="text-sm font-medium text-slate-800">数据显示货币</div>
                <div className="text-xs text-slate-400">当前：人民币 (¥)</div>
              </div>
              <select className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-400">
                <option value="cny">¥ 人民币</option>
                <option value="usd">$ 美元</option>
                <option value="hkd">HK$ 港币</option>
              </select>
            </div>
          </div>
        </SectionCard>
      </div>
    </PageShell>
  );
};

export default SettingsPage;
